import { forwardRef } from "react";
import {
  Notification,
  UsersNotificationSubscriptionStatus,
} from "@/models/notification/Notifications";
import {
  getNotificationCategory,
  getNotificationText,
} from "@/utils/notificationTexts";
import { formatDateToDanishLocaleOnlyDateShortMonthWithTime } from "@/utils/format";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { X } from "lucide-react";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  navigateToNotificationEntity,
  typesThatDoesntNavigate,
} from "@/services/notificationNavigationService";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  notification: Notification;
  dismissNotification: (notificationId: string) => void;
  isDismissingNotification: Record<string, boolean> | undefined;
  onNavigate?: () => void;
};

const NotificationItem = forwardRef<HTMLDivElement, Props>(
  (
    { notification, dismissNotification, isDismissingNotification, onNavigate },
    ref
  ) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const goToEntity = async (
      notification: Notification,
      event?: React.MouseEvent
    ) => {
      const navigationPromise = navigateToNotificationEntity(
        notification,
        navigate,
        queryClient,
        event
      );

      if (
        (notification.status === UsersNotificationSubscriptionStatus.Active ||
          notification.status === UsersNotificationSubscriptionStatus.Seen) &&
        !typesThatDoesntNavigate.includes(notification.type)
      ) {
        dismissNotification(notification.id);
      }

      // Close the popover when navigating
      if (!typesThatDoesntNavigate.includes(notification.type)) {
        onNavigate?.();
      }

      await navigationPromise;
    };

    return (
      <div
        ref={ref}
        className="flex flex-col hover:bg-accent p-2 rounded-md gap-1 m-1 group transition-all duration-200 cursor-pointer"
        onClick={(event) => goToEntity(notification, event)}
      >
        <div className="flex w-full text-[10px] text-muted-foreground group-hover:text-accent-foreground/70 justify-between items-center">
          <span className="flex-shrink-0">
            {formatDateToDanishLocaleOnlyDateShortMonthWithTime(
              new Date(notification.createdAt)
            )}
          </span>
          <span className="flex-shrink-0">
            {getNotificationCategory(notification)}
          </span>
        </div>
        <div className="flex w-full justify-between items-center relative">
          <div className="text-[12px] font-medium mr-4 group-hover:text-accent-foreground">
            {getNotificationText(notification)}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-5 p-1 hover:bg-muted absolute -bottom-1 -right-0.5 opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              dismissNotification(notification.id);
            }}
            disabled={isDismissingNotification?.[notification.id]}
          >
            {isDismissingNotification?.[notification.id] ? (
              <Spinner className="size-4" />
            ) : (
              <X className="size-4" />
            )}
          </Button>
        </div>
      </div>
    );
  }
);

NotificationItem.displayName = "NotificationItem";

export default NotificationItem;
